import React, { useState, useEffect } from 'react';
import '../../styles/Boutique/Cart.css';

const PaymentModal = ({ isOpen, closeModal, cart }) => {
  const API_BASE_URL = process.env.NODE_ENV === 'development'
    ? 'http://localhost'
    : 'https://museedestroupesdemarine.fr';

  const API_ENDPOINT = '/api/index.php?endpoint=create-payment';

  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    address: '',
    city: '',
    postcode: ''
  });
  const [statusMessage, setStatusMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('no-scroll');
      setStatusMessage('');
      setError(null);
    } else {
      document.body.classList.remove('no-scroll');
    }

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const validateForm = () => {
    const requiredFields = ['firstName', 'lastName', 'email', 'mobileNumber', 'address', 'city', 'postcode'];
    const missingFields = requiredFields.filter(field => !customerInfo[field]);

    if (missingFields.length > 0) {
      setError(`Veuillez remplir tous les champs obligatoires`);
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerInfo.email)) {
      setError('Veuillez entrer une adresse email valide');
      return false;
    }

    const phoneRegex = /^(0|\+33)[1-9][0-9]{8}$/;
    if (!phoneRegex.test(customerInfo.mobileNumber)) {
      setError('Veuillez entrer un numéro de téléphone valide (format: 0XXXXXXXXX ou +33XXXXXXXXX)');
      return false;
    }

    return true;
  };

  const calculateShippingCost = (cart) => {
    const totalWeight = cart.reduce((total, item) => {
      const itemWeight = item.weight || 0;
      return total + itemWeight * item.quantity;
    }, 0);

    let shippingCost = 0;

    if (cart.length === 1 && cart[0].packagingFee) {
      shippingCost = cart[0].packagingFee;
    } else {
      if (totalWeight <= 250) {
        shippingCost = 6.75;
      } else if (totalWeight <= 500) {
        shippingCost = 8.85;
      } else if (totalWeight <= 750) {
        shippingCost = 10.65;
      } else if (totalWeight <= 1000) {
        shippingCost = 11.40;
      } else if (totalWeight <= 2000) {
        shippingCost = 12.70;
      } else if (totalWeight <= 5000) {
        shippingCost = 20.60;
      } else {
        shippingCost = 25.00;
      }
    }

    return shippingCost;
  };

  const formatMobileNumber = (mobileNumber) => {
    if (!mobileNumber) return '';
    if (mobileNumber.startsWith('0')) {
      return `+33${mobileNumber.substring(1)}`;
    }
    return mobileNumber;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setStatusMessage('Préparation du paiement...');

    try {
      const amount = cart.reduce((total, item) => total + (item.price || 0) * item.quantity, 0);
      const shippingCost = calculateShippingCost(cart);
      const totalAmount = amount + shippingCost;
      const customerId = `customer_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

      const paymentData = {
        email: customerInfo.email,
        amount: totalAmount,
        customer_id: customerId,
        firstName: customerInfo.firstName,
        lastName: customerInfo.lastName,
        mobileNumber: formatMobileNumber(customerInfo.mobileNumber),
        address: customerInfo.address,
        city: customerInfo.city,
        postcode: customerInfo.postcode,
        cart: JSON.stringify(cart),
        shippingCost,
        cartDetails: cart.map(item => ({
          title: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
          selectedSize: item.selectedSize || null
        }))
      };

      const fullUrl = `${API_BASE_URL}${API_ENDPOINT}`;

      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(paymentData),
        mode: 'cors',
        credentials: 'include'
      };

      const response = await fetch(fullUrl, fetchOptions);

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") === -1) {
        const textResponse = await response.text();
        console.error('Réponse non-JSON reçue:', textResponse);
        throw new Error(`Réponse non valide du serveur: format attendu JSON, reçu ${contentType}`);
      }

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erreur response:', errorData);
        throw new Error(`Erreur serveur: ${response.status} - ${errorData.error || 'Erreur inconnue'}`);
      }

      const result = await response.json();

      if (result && result.payment_url && result.payment_id) {
        setStatusMessage('Redirection vers la page de paiement...');
        localStorage.setItem('current_payment_id', result.payment_id);
        localStorage.setItem('current_customer_id', customerId);
        window.location.href = result.payment_url;
      } else {
        setStatusMessage('');
        setError('Erreur: La réponse de paiement ne contient pas l\'URL nécessaire.');
      }
    } catch (error) {
      setStatusMessage('');
      setError(`Erreur lors de la préparation du paiement: ${error.message}`);
      console.error('Error creating payment:', error);
    } finally {
      setLoading(false);
    }
  };

  const cartTotal = cart.reduce((total, item) => total + (item.price || 0) * item.quantity, 0);
  const shippingCost = calculateShippingCost(cart);
  const orderTotal = cartTotal + shippingCost;

  return (
    <div className={`modal ${isOpen ? 'show' : ''}`}>
      <div className="modal-content">
        <span className="close" onClick={closeModal}>&times;</span>
        <h2>Finaliser votre commande</h2>

        <div className="order-summary">
          <h3>Récapitulatif de votre commande</h3>
          <table>
            <tbody>
              <tr>
                <td>Total articles:</td>
                <td>{cartTotal.toFixed(2)} €</td>
              </tr>
              <tr>
                <td>Frais de port:</td>
                <td>{shippingCost.toFixed(2)} €</td>
              </tr>
              <tr className="total-row">
                <td><strong>Total à payer:</strong></td>
                <td><strong>{orderTotal.toFixed(2)} €</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        <form id="customerForm" onSubmit={handleSubmit}>
          <h3>Informations de livraison</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customerFirstName">Prénom:<span className="required">*</span></label>
              <input
                type="text"
                id="customerFirstName"
                name="firstName"
                value={customerInfo.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="customerLastName">Nom:<span className="required">*</span></label>
              <input
                type="text"
                id="customerLastName"
                name="lastName"
                value={customerInfo.lastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="customerEmail">Email:<span className="required">*</span></label>
            <input
              type="email"
              id="customerEmail"
              name="email"
              value={customerInfo.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="customerMobileNumber">Téléphone:<span className="required">*</span></label>
            <input
              type="text"
              id="customerMobileNumber"
              name="mobileNumber"
              value={customerInfo.mobileNumber}
              onChange={handleChange}
              placeholder="0XXXXXXXXX ou +33XXXXXXXXX"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="customerAddress">Adresse:<span className="required">*</span></label>
            <input
              type="text"
              id="customerAddress"
              name="address"
              value={customerInfo.address}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customerCity">Ville:<span className="required">*</span></label>
              <input
                type="text"
                id="customerCity"
                name="city"
                value={customerInfo.city}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="customerPostcode">Code Postal:<span className="required">*</span></label>
              <input
                type="text"
                id="customerPostcode"
                name="postcode"
                value={customerInfo.postcode}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="status-message">
            {statusMessage && <div className="status">{statusMessage}</div>}
          </div>

          <button
            className="checkout-button"
            type="submit"
            disabled={loading}
          >
            {loading ? 'Traitement en cours...' : 'Procéder au paiement'}
          </button>
        </form>

        <div className="payment-info">
          <p>Paiement sécurisé par PayPlug</p>
          <p>Vous allez être redirigé vers notre partenaire de paiement sécurisé.</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;